/**
 * A student's boxes in the person visit (P09 to P11): their academic standing,
 * the student record's facts saved where they are drawn, Change student status
 * opened in place with a reason (K2, O3), the academic record whose course rows
 * visit the learner's marks (`enroll:`), and where an imported record came from.
 * A failed academic read draws no GPA and no course, never an empty record (R3).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, BoxState, FactBox, Figure, ListBox, Read, TaskBox } from "@/components/ui/box.schema";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { academicStructureApi } from "@/lib/api/academicStructure";
import { currentOnConflict } from "@/lib/api/conflict";
import { MODALITY_LABEL, type Modality } from "@/lib/api/courses";
import { PEOPLE_QUERY_KEY, type PeopleDetailResponse, type PeopleStudentRecord } from "@/lib/api/people";
import {
  academicFigure,
  academicRecordQuery,
  correctStudentConversion,
  getPostAdmissionReadiness,
  patchStudent,
  restoreStudent,
  setStudentStanding,
  withdrawStudent,
  type AcademicRecord,
  type AcademicRecordCourse,
  type AcademicRecordTerm,
  type ConversionRestoreStage,
  type PostAdmissionReadiness,
  type StudentDetail,
  type StudentStanding,
} from "@/lib/api/students";
import { TIME_OPTIONS, WEEKDAY_OPTIONS } from "@/lib/course-form";
import { courseTimezoneLabel } from "@/lib/course-schedule";

import { studentCopy as copy, studentText } from "./student-copy";
import { peopleWords as words } from "../people-copy";
import { formatDay, statusLabel } from "../person-record";
import { CORRECTION_STAGES, GENDERS, ISSUANCE_SOURCE_LABEL, STANDINGS, genderLabel } from "../student/student-words";
import { imam, queryRead, readOf, type PendingCommit, useFieldSaves, useTaskCommit } from "./commit";
import { resultState } from "./access-boxes";
import { savedField, type FieldOption, type FieldSpec } from "./fields";

const box = <T,>(spec: FactBox<T> | ListBox<T, unknown> | TaskBox<T>) => spec as unknown as Box;
const OPEN = { does: "open", verb: words.open } as const;

/* ------------------------------------------------------------------ */
/* The student record's facts, saved where they are drawn              */
/* ------------------------------------------------------------------ */

const FIELDS = ["program_id", "cohort_id", "gender", "phone", "city", "country", "address", "emergency_contact", "citizenship_note", "note"] as const;
type StudentField = (typeof FIELDS)[number];

/** A version of the student record as its fields read it: the facts and the registrar note, which moves its version. */
interface StudentFacts {
  id: string;
  updated_at: string;
  program_name: string | null;
  cohort_name: string | null;
  values: Record<StudentField, string>;
}

function factsOfRecord(record: PeopleStudentRecord): StudentFacts {
  return {
    id: record.id,
    updated_at: record.updated_at,
    program_name: record.program_name,
    cohort_name: record.cohort_name,
    values: {
      program_id: record.program_id ?? "",
      cohort_id: record.cohort_id ?? "",
      gender: record.gender ?? "",
      phone: record.phone ?? "",
      city: record.city ?? "",
      country: record.country ?? "",
      address: record.address ?? "",
      emergency_contact: record.emergency_contact ?? "",
      citizenship_note: record.citizenship_note ?? "",
      note: record.internal_note?.note ?? "",
    },
  };
}

function factsOfDetail(detail: StudentDetail): StudentFacts {
  const profile = detail.profile;
  return {
    id: profile.id,
    updated_at: profile.updated_at,
    program_name: profile.program_name,
    cohort_name: profile.cohort_name,
    values: {
      program_id: profile.program_id ?? "",
      cohort_id: profile.cohort_id ?? "",
      gender: profile.gender ?? "",
      phone: profile.phone ?? "",
      city: profile.city ?? "",
      country: profile.country ?? "",
      address: profile.address ?? "",
      emergency_contact: profile.emergency_contact ?? "",
      citizenship_note: profile.citizenship_note ?? "",
      note: detail.internal_note?.note ?? "",
    },
  };
}

/* ------------------------------------------------------------------ */
/* The academic record's rows                                          */
/* ------------------------------------------------------------------ */

type AcademicRow =
  | { kind: "term"; key: string; year: string; term: AcademicRecordTerm }
  | { kind: "course"; key: string; course: AcademicRecordCourse };

function academicRows(record: AcademicRecord): AcademicRow[] {
  return record.years.flatMap((year) =>
    year.terms.flatMap((term) => [
      { kind: "term" as const, key: `term-${year.key}-${term.semester_id ?? "none"}`, year: year.label, term },
      ...term.courses.map((course) => ({ kind: "course" as const, key: course.enrollment_id, course })),
    ]),
  );
}

function time(value: string): string {
  const hhmm = value.slice(0, 5);
  return TIME_OPTIONS.find((option) => option.value === hhmm)?.label ?? hhmm;
}

function scheduleWords(schedule: AcademicRecordCourse["schedule"]): string {
  const days = [...(schedule.weekdays ?? [])]
    .sort((a, b) => a - b)
    .map((day) => WEEKDAY_OPTIONS.find(([value]) => value === day)?.[1])
    .filter(Boolean)
    .join(", ");
  const zone = courseTimezoneLabel(schedule.timezone);
  const hours = schedule.starts_at && schedule.ends_at ? studentText.hours(time(schedule.starts_at), time(schedule.ends_at), zone) : "";
  const modality = schedule.modality ? (MODALITY_LABEL[schedule.modality as Modality] ?? null) : null;
  return [days, hours, schedule.location, modality].filter(Boolean).join(" · ");
}

function courseFacts(course: AcademicRecordCourse): string {
  const teachers = course.instructors.length ? course.instructors.map((instructor) => instructor.name).join(", ") : copy.noInstructor;
  const credits = course.credits === null ? copy.noCredits : studentText.credits(course.credits);
  return [teachers, scheduleWords(course.schedule), credits].filter(Boolean).join(" · ");
}

export function courseState(course: AcademicRecordCourse): BoxState {
  const { result } = course;
  if (result.state === "withdrawn") return { class: "quiet", word: copy.withdrawn };
  if (course.participation_kind === "audit") return { class: "quiet", word: copy.audit };
  if (result.state === "certified" && result.letter) return { class: "done", word: result.letter };
  if (result.state === "awaiting_certification") return { class: "waiting", word: copy.awaitingCertification };
  return { class: "reading", word: copy.inProgress };
}

function termFacts(term: AcademicRecordTerm): string {
  if (term.gpa !== null) return studentText.termGpa(academicFigure(term.gpa), term.provisional);
  const counted = term.courses.filter((course) => course.participation_kind === "credit" && course.result.state !== "withdrawn");
  if (counted.some((course) => course.result.state === "in_progress")) return copy.inProgress;
  if (counted.some((course) => course.result.state === "awaiting_certification")) return copy.awaitingCertification;
  return copy.noGpaCredits;
}

function termState(term: AcademicRecordTerm): BoxState {
  if (term.status === "closed" && term.gpa !== null && !term.provisional) return { class: "done", word: copy.certified };
  return { class: "quiet", word: term.status === "active" ? copy.currentTerm : copy.inProgress };
}

/* ------------------------------------------------------------------ */
/* The boxes                                                           */
/* ------------------------------------------------------------------ */

interface StatusDraft {
  status: StudentStanding | "withdrawn" | "";
  reason: string;
}

interface CorrectDraft {
  stage: ConversionRestoreStage;
  reason: string;
}

type Provenance = NonNullable<PeopleStudentRecord["import_provenance"]>;
type ProvenanceRow =
  | { kind: "identity"; key: string; identity: Provenance["identities"][number] }
  | { kind: "receipt"; key: string; receipt: Provenance["receipts"][number] };

export function useStudentBoxes(personId: string | null, detailRead: Read<PeopleDetailResponse>): Box[] {
  const client = useQueryClient();
  const detail = detailRead.status === "ready" ? detailRead.value : detailRead.status === "stale" ? detailRead.last : null;
  const record = personId ? (detail?.student_record ?? null) : null;
  const studentId = record?.id ?? null;
  const sid = studentId ?? "none";

  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ["students"] }),
    client.invalidateQueries({ queryKey: ["student", studentId] }),
    client.invalidateQueries({ queryKey: ["post-admission-readiness", studentId] }),
    client.invalidateQueries({ queryKey: ["accounts"] }),
  ]);

  const academic = useQuery({ ...academicRecordQuery(sid), enabled: Boolean(studentId), retry: false });
  const programs = useQuery({ queryKey: ["programs"], queryFn: () => academicStructureApi.getPrograms(), enabled: Boolean(studentId) });
  const cohorts = useQuery({ queryKey: ["cohorts", "all"], queryFn: () => academicStructureApi.getCohorts({ status: "all" }), enabled: Boolean(studentId) });
  const readiness = useQuery({
    queryKey: ["post-admission-readiness", studentId],
    queryFn: () => getPostAdmissionReadiness(sid),
    enabled: Boolean(studentId && record?.status === "active"),
    retry: false,
  });

  const facts = record ? factsOfRecord(record) : null;
  const saves = useFieldSaves<StudentFacts, StudentField>({
    record: facts,
    identity: (value) => value.id,
    version: (value) => value.updated_at,
    fields: FIELDS,
    read: (value, field) => value.values[field],
    send: async (field, value, base, key) =>
      factsOfDetail(await patchStudent(base.id, { [field]: value.trim() === "" ? null : value, expected_updated_at: base.updated_at }, key)),
    conflictOf: (error) => {
      const current = currentOnConflict<StudentDetail>(error);
      return current ? factsOfDetail(current) : null;
    },
    reread: async () => {
      await refresh();
      const fresh = client.getQueryData<PeopleDetailResponse>([...PEOPLE_QUERY_KEY, "detail", personId, null]);
      if (!fresh?.student_record) throw new Error("unread");
      return factsOfRecord(fresh.student_record);
    },
    onSaved: () => void refresh(),
  });

  const status = useTabDraft<StatusDraft, PendingCommit>({
    box: "student-status",
    record: `person:${personId ?? "none"}`,
    version: record?.updated_at ?? null,
    initial: { status: "", reason: "" },
  });
  const correct = useTabDraft<CorrectDraft, PendingCommit>({
    box: "student-correct",
    record: `person:${personId ?? "none"}`,
    version: record?.updated_at ?? null,
    initial: { stage: "Applied", reason: "" },
  });
  const statusCommit = useTaskCommit({
    subject: `person:${personId ?? "none"}`,
    words: { refused: copy.statusRefused, unknown: copy.statusUnknown, unknownSentence: copy.unknownSentence, checkNote: copy.checkNote },
    refresh,
    keep: status,
  });
  const correctCommit = useTaskCommit({
    subject: `person:${personId ?? "none"}`,
    words: { refused: copy.correctRefused, unknown: copy.correctUnknown, unknownSentence: copy.unknownSentence, checkNote: copy.checkNote },
    refresh,
    keep: correct,
  });

  if (!personId || !record || !facts || !detail) return [];
  const at = (id: string) => `${id}-${personId}`;
  const recordRead = readOf(detailRead, (value) => value.student_record as PeopleStudentRecord);

  /* -------- Academic standing -------- */
  const academicRead = queryRead(academic, copy.academicUnavailable, copy.standingUnavailableDetail);
  const standing = box<AcademicRecord>({
    id: at("standing"),
    kind: "fact",
    job: copy.standingJob,
    label: copy.standingLabel,
    owner: imam,
    fold: "choice",
    read: academicRead,
    figures: (value) => {
      const figures: Figure[] = [];
      if (value.cumulative.gpa !== null) figures.push({ value: Number(academicFigure(value.cumulative.gpa)), unit: copy.cumulativeGpa });
      figures.push({ value: value.cumulative.certified_credits, unit: copy.certifiedCredits });
      return figures;
    },
    detail: (value) =>
      value.standing.state === "none" ? value.standing.label : studentText.standing(value.standing.label, academicFigure(value.standing.floor)),
    sub: (value) => (value.cumulative.provisional ? copy.provisional : ""),
  });

  /* -------- The student record -------- */
  const programOptions: FieldOption[] = (programs.data ?? []).map((program) => ({ value: program.id, label: program.name }));
  if (record.program_id && !programOptions.some((option) => option.value === record.program_id))
    programOptions.push({ value: record.program_id, label: record.program_name ?? copy.thisProgram });
  const cohortOptions: FieldOption[] = (cohorts.data ?? [])
    .filter((cohort) => cohort.status === "active" || cohort.id === record.cohort_id)
    .map((cohort) => ({ value: cohort.id, label: cohort.status === "active" ? cohort.label : studentText.archived(cohort.label) }));
  if (record.cohort_id && !cohortOptions.some((option) => option.value === record.cohort_id))
    cohortOptions.push({ value: record.cohort_id, label: record.cohort_name ?? copy.thisCohort });
  const named = (options: FieldOption[], value: string, fallback: string) =>
    value ? (options.find((option) => option.value === value)?.label ?? fallback) : copy.unassigned;
  const field = (name: StudentField, label: string, extra: Partial<FieldSpec<StudentFacts, StudentField>> = {}) =>
    savedField(saves, { id: at(`student-${name}`), field: name, label, theirs: (theirs) => theirs.values[name], ...extra });
  const issuance = record.sid_issuance;
  const student = box<PeopleStudentRecord>({
    id: at("student"),
    kind: "fact",
    job: copy.studentJob,
    label: copy.studentLabel,
    owner: imam,
    fold: "choice",
    act: OPEN,
    read: recordRead,
    prose: (value) => [value.program_name, value.cohort_name].filter(Boolean).join(" · ") || copy.unassigned,
    detail: (value) => studentText.sidAndStatus(value.sid, statusLabel(value.status) ?? value.status),
    sub: () =>
      issuance === undefined
        ? ""
        : issuance === null
          ? copy.issuedOutsideSequence
          : studentText.issued(formatDay(issuance.issued_at), issuance.cohort_year, ISSUANCE_SOURCE_LABEL[issuance.cohort_year_source]),
    fields: () => [
      field("program_id", copy.program, { theirs: (theirs) => theirs.program_name ?? named(programOptions, theirs.values.program_id, copy.anotherProgram), options: { empty: copy.unassigned, choices: programOptions } }),
      field("cohort_id", copy.cohort, { theirs: (theirs) => theirs.cohort_name ?? named(cohortOptions, theirs.values.cohort_id, copy.anotherCohort), options: { empty: copy.unassigned, choices: cohortOptions } }),
      field("gender", copy.gender, { theirs: (theirs) => genderLabel(theirs.values.gender || null), options: { empty: words.notRecorded, choices: GENDERS } }),
      field("phone", copy.phone, { type: "tel" }),
      field("city", copy.city),
      field("country", copy.country),
      field("address", copy.address),
      field("emergency_contact", copy.emergencyContact),
      field("citizenship_note", copy.citizenshipNote, { multiline: true }),
      field("note", copy.registrarNote, { multiline: true, note: copy.registrarNoteScope }),
    ],
  });

  /* -------- Change student status -------- */
  const withdrawn = record.status === "withdrawn";
  const chosen = status.value.status;
  const statusWhy = at("status-why");
  const statusPart = at("status-to");
  const statusEffects = (): string[] => {
    if (withdrawn) return [copy.restoreEffect, copy.enrollmentsNotReinstated];
    if (chosen === "withdrawn") return [copy.withdrawEffect, copy.withdrawCloses, copy.withdrawSuspends];
    if (chosen) return [studentText.becomes(statusLabel(chosen) ?? chosen), copy.statusKeeps];
    return [];
  };
  const statusBlockers = (): Blocker[] => [
    ...(!withdrawn && !chosen ? [{ kind: "missing" as const, part: statusPart, sentence: copy.chooseStatus }] : []),
    ...(!status.value.reason.trim() ? [{ kind: "missing" as const, part: statusWhy, sentence: copy.sayWhy }] : []),
  ];
  const choices = [...STANDINGS.filter((each) => each !== record.status), "withdrawn" as const];
  const statusBox = box<PeopleStudentRecord>({
    id: at("status"),
    kind: "task",
    job: copy.statusJob,
    label: withdrawn ? copy.restoreLabel : copy.statusLabel,
    owner: imam,
    fold: "choice",
    read: recordRead,
    detail: () => (withdrawn ? copy.restoreDetail : copy.statusDetail),
    openDetail: (value) => (withdrawn || !chosen ? undefined : studentText.fromTo(statusLabel(value.status) ?? value.status, statusLabel(chosen) ?? chosen)),
    state: (value) =>
      resultState(
        statusCommit.result,
        status.kept ? { class: "draft", word: statusBlockers().length ? copy.reasonNeeded : copy.readyToChange } : { class: "quiet", word: statusLabel(value.status) ?? value.status },
      ),
    act: { does: "open", verb: withdrawn ? copy.restoreLabel : copy.changeStatus },
    candidate: { eligible: true, blocked: null, rank: null },
    parts: [
      ...(withdrawn
        ? []
        : [{
            id: statusPart,
            label: copy.newStatus,
            state: chosen ? { class: "done" as const, word: statusLabel(chosen) ?? chosen } : { class: "waiting" as const, word: words.needed },
            owner: imam,
            fold: "never" as const,
            draft: "browser" as const,
            keep: status,
            render: () => (
              <div className="grid gap-1.5">
                <Label htmlFor={`${statusPart}-select`}>{copy.newStatus}</Label>
                <NativeSelect
                  id={`${statusPart}-select`}
                  value={chosen}
                  onChange={(event) => {
                    statusCommit.reset();
                    status.set((was) => ({ ...was, status: event.target.value as StatusDraft["status"] }));
                  }}
                >
                  <option value="">{copy.chooseStatusOption}</option>
                  {choices.map((each) => <option key={each} value={each}>{statusLabel(each) ?? each}</option>)}
                </NativeSelect>
              </div>
            ),
          }]),
      {
        id: statusWhy,
        label: words.whyPart,
        state: status.value.reason.trim() ? { class: "done", word: words.written } : { class: "waiting", word: copy.reasonNeeded },
        owner: imam,
        fold: "never",
        draft: "browser",
        keep: status,
        render: () => (
          <div className="grid gap-1.5">
            <Label htmlFor={`${statusWhy}-text`}>{copy.whyStatus}</Label>
            <Textarea
              id={`${statusWhy}-text`}
              rows={3}
              value={status.value.reason}
              onChange={(event) => {
                statusCommit.reset();
                status.set((was) => ({ ...was, reason: event.target.value }));
              }}
            />
          </div>
        ),
      },
    ],
    commit: {
      verb: withdrawn ? copy.restoreLabel : chosen === "withdrawn" ? copy.withdrawVerb : copy.statusLabel,
      cap: withdrawn ? copy.restoreCap : copy.statusCap,
      effects: () => statusEffects().map((sentence, index) => ({ id: `effect-${index}`, sentence })),
      blockers: statusBlockers,
      run: (key) =>
        statusCommit.run(key, {
          effects: statusEffects(),
          write: async (commitKey) => {
            const reason = status.value.reason.trim();
            if (withdrawn) await restoreStudent(record.id, reason, commitKey, record.updated_at);
            else if (chosen === "withdrawn") await withdrawStudent(record.id, reason, commitKey, record.updated_at);
            else return (await setStudentStanding(record.id, chosen as StudentStanding, reason, commitKey, record.updated_at)).profile.updated_at;
          },
        }),
    },
    result: statusCommit.result,
    draft: status.kept,
  });

  /* -------- Correct a mistaken conversion -------- */
  const boxes: Box[] = [standing, student, statusBox];
  const admitted: PostAdmissionReadiness | undefined = readiness.data;
  if (admitted && admitted.admission.state === "recorded" && admitted.admission.stage === "Admitted" && record.status === "active") {
    const stagePart = at("correct-stage");
    const why = at("correct-why");
    const effects = [studentText.returnTo(correct.value.stage), copy.correctWithdraws, copy.correctKeepsSid];
    const blockers = (): Blocker[] => (correct.value.reason.trim() ? [] : [{ kind: "missing", part: why, sentence: copy.sayWhyCorrect }]);
    boxes.push(box<PostAdmissionReadiness>({
      id: at("correct"),
      kind: "task",
      job: copy.correctJob,
      label: copy.correctLabel,
      owner: imam,
      fold: "choice",
      read: queryRead(readiness, copy.readinessUnavailable),
      detail: () => copy.correctDetail,
      state: () => resultState(correctCommit.result, correct.kept ? { class: "draft", word: blockers().length ? copy.reasonNeeded : copy.readyToCorrect } : { class: "quiet", word: copy.admitted }),
      act: { does: "open", verb: copy.correctLabel },
      candidate: { eligible: true, blocked: null, rank: null },
      parts: [
        {
          id: stagePart,
          label: copy.returnApplicationTo,
          state: { class: "done", word: correct.value.stage },
          owner: imam,
          fold: "never",
          draft: "browser",
          keep: correct,
          render: () => (
            <div className="grid gap-1.5">
              <Label htmlFor={`${stagePart}-select`}>{copy.returnApplicationTo}</Label>
              <NativeSelect
                id={`${stagePart}-select`}
                value={correct.value.stage}
                onChange={(event) => {
                  correctCommit.reset();
                  correct.set((was) => ({ ...was, stage: event.target.value as ConversionRestoreStage }));
                }}
              >
                {CORRECTION_STAGES.map((each) => <option key={each} value={each}>{each}</option>)}
              </NativeSelect>
            </div>
          ),
        },
        {
          id: why,
          label: words.whyPart,
          state: correct.value.reason.trim() ? { class: "done", word: words.written } : { class: "waiting", word: copy.reasonNeeded },
          owner: imam,
          fold: "never",
          draft: "browser",
          keep: correct,
          render: () => (
            <div className="grid gap-1.5">
              <Label htmlFor={`${why}-text`}>{copy.whyCorrect}</Label>
              <Textarea
                id={`${why}-text`}
                rows={3}
                value={correct.value.reason}
                onChange={(event) => {
                  correctCommit.reset();
                  correct.set((was) => ({ ...was, reason: event.target.value }));
                }}
              />
            </div>
          ),
        },
      ],
      commit: {
        verb: copy.correctVerb,
        cap: copy.correctCap,
        effects: () => effects.map((sentence, index) => ({ id: `effect-${index}`, sentence })),
        blockers,
        run: (key) =>
          correctCommit.run(key, {
            effects,
            write: async (commitKey) => void (await correctStudentConversion(record.id, correct.value.reason.trim(), correct.value.stage, commitKey, record.updated_at)),
          }),
      },
      result: correctCommit.result,
      draft: correct.kept,
    }));
  }

  /* -------- The academic record -------- */
  boxes.push(box<AcademicRecord>({
    id: at("academic"),
    kind: "list",
    job: copy.academicJob,
    label: copy.academicLabel,
    owner: imam,
    fold: "choice",
    act: OPEN,
    // A record with no term or course yet is an empty List, which says so (R1).
    read: academicRead.status === "failed"
      ? { ...academicRead, detail: copy.academicUnavailableDetail }
      : academicRead.status === "ready" && academicRows(academicRead.value).length === 0
        ? { status: "empty", sentence: copy.noCoursesYet }
        : academicRead,
    detail: (value) => studentText.yearsRead(value.years.map((year) => year.label)),
    state: (value) => {
      const courses = academicRows(value).filter((row) => row.kind === "course").length;
      return courses ? { class: "quiet", word: studentText.coursesAcross(courses, value.years.length) } : { class: "quiet", word: copy.noCoursesYet };
    },
    rows: academicRows,
    row: {
      key: (row) => row.key,
      title: (row) => (row.kind === "term" ? studentText.term(row.year, row.term.name ?? copy.noTerm) : studentText.course(row.course.code, row.course.title)),
      facts: (row) => (row.kind === "term" ? termFacts(row.term) : courseFacts(row.course)),
      state: (row) => (row.kind === "term" ? termState(row.term) : courseState(row.course)),
      act: (row) =>
        row.kind === "course" ? { does: "visit", verb: copy.marksAndAttendance, visit: `enroll:${personId}/${row.course.course_id}` } : undefined,
    },
  } as ListBox<AcademicRecord, AcademicRow>));

  /* -------- Recorded from a roster, or imported history -------- */
  const provenance = record.import_provenance;
  if (provenance?.state === "imported") {
    const roster = provenance.identities.some((identity) => identity.kind === "registrar_roster");
    const rows: ProvenanceRow[] = [
      ...provenance.identities.map((identity) => ({ kind: "identity" as const, key: `${identity.first_batch_id}-${identity.import_key}`, identity })),
      ...provenance.receipts.map((receipt) => ({ kind: "receipt" as const, key: receipt.id, receipt })),
    ];
    boxes.push(box<Provenance>({
      id: at("imported"),
      kind: "list",
      job: copy.provenanceJob,
      label: roster ? copy.rosterLabel : copy.importedLabel,
      owner: imam,
      fold: "choice",
      act: OPEN,
      read: readOf(recordRead, () => provenance),
      detail: () => copy.provenanceDetail,
      state: () => ({ class: "quiet", word: studentText.provenanceCount(provenance.identities.length, provenance.receipts.length, roster) }),
      rows: () => rows,
      row: {
        key: (row) => row.key,
        title: (row) =>
          row.kind === "identity"
            ? row.identity.kind === "registrar_roster"
              ? studentText.recordedFromRoster(row.identity.source_name, formatDay(row.identity.imported_at))
              : row.identity.source_name
            : studentText.receipt(row.receipt.mode === "dry_run", row.receipt.action, formatDay(row.receipt.created_at)),
        facts: (row) =>
          row.kind === "identity"
            ? studentText.sourceKey(row.identity.kind === "registrar_roster", row.identity.import_key, formatDay(row.identity.imported_at))
            : [row.receipt.reason, row.receipt.corrects_receipt_id ? copy.correctsReceipt : null].filter(Boolean).join(" · "),
        act: (row) =>
          row.kind === "identity"
            ? { does: "visit", verb: copy.openFirstBatch, visit: `run:${row.identity.first_batch_id}` }
            : { does: "visit", verb: copy.openReceiptBatch, visit: `import-receipt:${row.receipt.batch_id}/${row.receipt.id}` },
      },
    } as ListBox<Provenance, ProvenanceRow>));
  }

  return boxes;
}
